import { formatFormatterChain } from "@imminently/decisively-core";
import { parseAttributeTemplate } from "../attributeTemplate";
import type { AttributeOption } from "../types";
import { attributeTokenTemplate, readTokenFormatters } from "./attributeToken";

/**
 * Conversions between the rich text editor's HTML and its raw template form (ENG-591).
 *
 * The raw form is the editor HTML with each `attributeToken` span written as its
 * `{{ key | token as "label" }}` string, e.g.
 * `<p>Expires on {{ occExpiry | date fullDate }}.</p>`. It's what the Raw view
 * edits, and it round-trips: `templateToHtml(htmlToTemplate(html))` gives back
 * the same tokens.
 */

const TOKEN_SELECTOR = "span[data-attribute-token]";

const escapeAttribute = (value: string) => value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

/** Editor HTML -> raw template. Needs a DOM (browser or jsdom). */
export function htmlToTemplate(html: string): string {
  const container = document.createElement("template");
  container.innerHTML = html;
  for (const span of container.content.querySelectorAll(TOKEN_SELECTOR)) {
    const text = attributeTokenTemplate({
      attributeKey: span.getAttribute("data-attribute-key") ?? "",
      formatters: readTokenFormatters(span),
      customLabel: span.getAttribute("data-custom-label") ?? "",
    });
    span.replaceWith(document.createTextNode(text));
  }
  return container.innerHTML;
}

/** The token's `data-formatter` attribute (with a leading space), or nothing when it has no formatters. */
const chainAttribute = (formatters: string[]) => {
  const chain = formatFormatterChain(formatters);
  return chain ? ` data-formatter="${escapeAttribute(chain)}"` : "";
};

const escapeText = (value: string) => value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * Raw template -> editor HTML. Accepts HTML with `{{ }}` tokens mixed in. Only
 * tokens whose key is in `attributes` become token spans; an unknown key stays
 * as literal text. With `plainText`, the text between tokens is escaped rather
 * than read as markup.
 */
export function templateToHtml(template: string, attributes: AttributeOption[], plainText = false): string {
  return parseAttributeTemplate(template, attributes)
    .map((segment) => {
      if (segment.kind === "text") return plainText ? escapeText(segment.value) : segment.value;
      const customLabel = segment.label ? ` data-custom-label="${escapeAttribute(segment.label)}"` : "";
      const migratedFrom = segment.migratedFrom ? ` data-migrated-from="${escapeAttribute(segment.migratedFrom)}"` : "";
      return `<span data-attribute-token data-attribute-key="${escapeAttribute(segment.key)}"${chainAttribute(segment.formatters)}${customLabel}${migratedFrom}></span>`;
    })
    .join("");
}

/**
 * Plain-text template -> editor HTML: one paragraph per line, so line breaks
 * survive the editor. The reverse is `editor.getText({ blockSeparator: "\n" })`.
 */
export function plainTemplateToHtml(template: string, attributes: AttributeOption[]): string {
  return template
    .split("\n")
    .map((line) => `<p>${templateToHtml(line, attributes, true)}</p>`)
    .join("");
}

// Tags the editor itself writes. Anything else in angle brackets (`<name>`, `a < b > c`) is plain text.
const EDITOR_TAG_RE = /<\/?(p|br|div|h[1-6]|ul|ol|li|blockquote|pre|strong|em|b|i|u|s|a|span|code)(\s[^>]*)?\/?>/i;

/** True when a stored value is HTML markup (as the editor writes it) rather than plain text. */
export const looksLikeHtml = (value: string): boolean => EDITOR_TAG_RE.test(value);
